import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocalStorage } from 'usehooks-ts';

import {
  amountTooHighValidationMessage,
  amountTooLowValidationMessage,
  amountValidationMessage,
  LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY,
  loanPurposeOptions,
  maxBorrowAmount,
  mergeClassNameStrings,
  minBorrowAmount,
  NcBorrowingNeedsFormT,
  snakeToKebabCase,
} from '@consumer-frontend/nc-utils';
import {
  Container,
  Fieldset,
  RadioGroup,
} from '@consumer-frontend/shared-ui';

import { useApplyRouting } from '../../routing';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';
import { NcInput, NcInputPropsT } from '../NcInput';
import { NcRadioCard } from '../NcRadioCard';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/apply/borrowing-needs.scss';

export const amountValidation = {
  max: {
    message: `${amountTooHighValidationMessage} ${amountValidationMessage}`,
    value: maxBorrowAmount,
  },
  min: {
    message: `${amountTooLowValidationMessage} ${amountValidationMessage}`,
    value: minBorrowAmount,
  },
  required: 'Enter the amount of money you would like to borrow.',
  valueAsNumber: true,
};

const amountInputProps: NcInputPropsT = {
  className: 'nc-loan-amount-input',
  helperText: amountValidationMessage,
  id: 'borrowing-amount',
  label: 'Amount',
  name: 'loanAmount',
  prefix: '$',
  type: 'number',
};

export type NcBorrowingNeedsPageT = React.FC;

export const NcBorrowingNeedsPage: NcBorrowingNeedsPageT = () => {
  const [loanAmountAndPurpose, storeLoanAmountAndPurpose] = useLocalStorage<Partial<NcBorrowingNeedsFormT>>(
    LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY,
    {},
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<NcBorrowingNeedsFormT>({ defaultValues: loanAmountAndPurpose });
  const selectedBorrowingNeed = watch('loanPurpose');

  const { handleContinue } = useApplyRouting();

  const onSubmit: SubmitHandler<NcBorrowingNeedsFormT> = async (data) => {
    storeLoanAmountAndPurpose(data);

    await handleContinue(data);
  };

  return (
    <Container className="nc-borrowing-needs-page" hasBoxShadow>
      <h1 className="h3">Borrowing Needs</h1>

      <NcForm onSubmit={handleSubmit(onSubmit)}>
        <NcInput
          {...register('loanAmount', amountValidation)}
          {...amountInputProps}
          errorMessage={errors.loanAmount?.message}
        />

        <RadioGroup
          controlId="nc-borrowing-purpose-options"
          errorMessage={errors.loanPurpose?.message}
          label="Purpose"
        >
          <div className={mergeClassNameStrings('radio-grid', errors.loanPurpose && 'is-invalid')}>
            {loanPurposeOptions.map((option) => {
              const {
                activeIconURL,
                iconURL,
                label,
                value,
              } = option;

              const kebabCaseName = snakeToKebabCase(value);

              return (
                <NcRadioCard
                  {...register('loanPurpose', { required: 'Select a reason for borrowing.' })}
                  activeIcon={activeIconURL}
                  alt={kebabCaseName}
                  checked={selectedBorrowingNeed === value}
                  className={mergeClassNameStrings(kebabCaseName, 'radio-card')}
                  groupError
                  icon={iconURL}
                  id={value}
                  key={value}
                  value={value}
                  label={label}
                />
              );
            })}
          </div>
        </RadioGroup>

        <Fieldset className="cta-buttons">
          <NcButton
            type="submit"
          >
            Continue
          </NcButton>
        </Fieldset>

      </NcForm>
    </Container>
  );
};
